/**
 * Guided tour overlay (QA F4). Renders the current step of the tour owned by
 * TutorialContext:
 *
 * - Opens the step's route first (static, or the user's latest report / first
 *   mentee resolved at run time), presses a tab when the step needs one, then
 *   looks for the step's testID anchors.
 * - Found: dims the screen around the element (spotlight) and places the step
 *   card next to it. Not found: a centered card that says where to go. Never a
 *   spotlight without a target.
 * - Next / Back / Skip, a step counter, Esc to skip, focus moves to the card,
 *   no motion when the user prefers reduced motion. Re-measures on resize and
 *   scroll, so phone and desktop layouts both stay aligned.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Platform, Pressable, StyleSheet, View, useWindowDimensions } from 'react-native';
import Animated, { FadeIn, useReducedMotion } from 'react-native-reanimated';
import { MaterialIcons } from '@expo/vector-icons';
import { usePathname, useRouter } from 'expo-router';

import { Text } from '@/components/ui/text';
import { reviewDesk } from '../../design-system/theme';
import { useTutorial } from '../../context/TutorialContext';
import { useTranslation } from '../../hooks/useTranslation';
import { trackTourStepViewed } from '../../services/analytics';
import {
  findTourElement,
  measureTourElement,
  pollFor,
  pressTourElement,
  revealTourElement,
} from '../../tour/domTargets';
import {
  isTourRoute,
  placeTourCard,
  stepPresentation,
  targetsFor,
  type TourRect,
} from '../../tour/tourModel';
import { resolveTourRoute } from '../../tour/tourRoutes';

type ViewMode = 'locating' | 'spotlight' | 'card';

const SPOTLIGHT_PADDING = 6;
const TARGET_TIMEOUT_MS = 5000;
const PREPARE_TIMEOUT_MS = 2500;
const NAVIGATION_TIMEOUT_MS = 4000;
const NAVIGATION_SETTLE_MS = 150;
const DIM = 'rgba(15, 23, 42, 0.58)';
const isWeb = Platform.OS === 'web';

function normalizePath(path: string | null | undefined): string {
  if (!path) return '/';
  return path.split('?')[0].replace(/\/+$/, '') || '/';
}

export function TutorialLayer() {
  const { t } = useTranslation();
  const router = useRouter();
  const pathname = usePathname();
  const reduceMotion = useReducedMotion();
  const { width: viewportWidth, height: viewportHeight } = useWindowDimensions();
  const {
    isActive,
    currentStep,
    currentStepIndex,
    totalSteps,
    role,
    isPhone,
    returnTo,
    nextStep,
    prevStep,
    skipTutorial,
  } = useTutorial();

  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;
  const routeCache = useRef(new Map<string, string | null>());
  const elementRef = useRef<HTMLElement | null>(null);
  const cardRef = useRef<View>(null);
  const viewedRef = useRef<string>('');
  // What the current step resolved to. Keyed by step so a new step never
  // renders, even for one frame, at the previous step's position.
  const [located, setLocated] = useState<{ key: string; mode: ViewMode }>({ key: '', mode: 'locating' });
  const [rect, setRect] = useState<TourRect | null>(null);
  const [cardHeight, setCardHeight] = useState(260);

  const stepKey = isActive && currentStep ? `${currentStepIndex}:${currentStep.id}` : '';
  const mode: ViewMode = located.key === stepKey ? located.mode : 'locating';

  useEffect(() => {
    if (!isActive) {
      routeCache.current.clear();
      elementRef.current = null;
      viewedRef.current = '';
    }
  }, [isActive]);

  // ── Open the step's screen and find its anchor ──
  useEffect(() => {
    if (!stepKey || !currentStep) return undefined;
    let cancelled = false;
    const pollers: { cancel: () => void }[] = [];
    elementRef.current = null;
    setRect(null);

    const showCard = () => {
      if (!cancelled) setLocated({ key: stepKey, mode: stepPresentation(false) });
    };

    (async () => {
      let route = currentStep.route ?? null;
      if (currentStep.dynamicRoute) {
        const kind = currentStep.dynamicRoute;
        if (!routeCache.current.has(kind)) {
          routeCache.current.set(kind, await resolveTourRoute(kind));
        }
        route = routeCache.current.get(kind) ?? null;
        // The user has no report / mentee yet: say where it will be.
        if (!route) return showCard();
      }
      if (cancelled) return;
      if (route && normalizePath(route) !== normalizePath(pathnameRef.current)) {
        router.push(route as never);
        // Wait for the new screen: the old one still holds look-alike anchors
        // (the shell menu), which would be measured just before unmounting.
        const target = normalizePath(route);
        const arrived = pollFor(() => (normalizePath(pathnameRef.current) === target ? true : null), NAVIGATION_TIMEOUT_MS);
        pollers.push(arrived);
        await arrived.promise;
        await new Promise((resolve) => setTimeout(resolve, NAVIGATION_SETTLE_MS));
      }
      if (currentStep.prepareClick) {
        const pressId = currentStep.prepareClick;
        const press = pollFor(() => (pressTourElement(pressId) ? true : null), PREPARE_TIMEOUT_MS);
        pollers.push(press);
        await press.promise;
      }
      if (cancelled) return;
      const candidates = targetsFor(currentStep, isPhone);
      if (candidates.length === 0) return showCard();
      const lookup = pollFor(() => findTourElement(candidates), TARGET_TIMEOUT_MS);
      pollers.push(lookup);
      const element = await lookup.promise;
      if (cancelled) return;
      if (!element) return showCard();
      revealTourElement(element, reduceMotion);
      // Let a scroll settle before measuring.
      await new Promise((resolve) => setTimeout(resolve, reduceMotion ? 0 : 220));
      if (cancelled) return;
      let shown = element;
      let measured = measureTourElement(shown);
      if (!measured) {
        // The element was replaced while settling (a re-render): look again.
        const retry = pollFor(() => findTourElement(candidates), 1500);
        pollers.push(retry);
        const again = await retry.promise;
        if (cancelled) return;
        measured = measureTourElement(again);
        if (again) shown = again;
      }
      if (!measured) return showCard();
      elementRef.current = shown;
      setRect(measured);
      setLocated({ key: stepKey, mode: stepPresentation(true) });
    })();

    return () => {
      cancelled = true;
      pollers.forEach((poller) => poller.cancel());
    };
    // Re-run per step (and when the layout switches between phone and desktop).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepKey, isPhone]);

  // ── Keep the spotlight on the element through resize, scroll and layout shifts ──
  useEffect(() => {
    if (!isWeb || mode !== 'spotlight' || !currentStep) return undefined;
    const update = () => {
      let element = elementRef.current;
      if (!element || !element.isConnected) {
        element = findTourElement(targetsFor(currentStep, isPhone));
        elementRef.current = element;
      }
      const measured = measureTourElement(element);
      if (!measured) {
        setRect(null);
        setLocated({ key: stepKey, mode: 'card' });
        return;
      }
      setRect((previous) => (
        previous
        && Math.abs(previous.x - measured.x) < 1
        && Math.abs(previous.y - measured.y) < 1
        && Math.abs(previous.width - measured.width) < 1
        && Math.abs(previous.height - measured.height) < 1
          ? previous
          : measured
      ));
    };
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    const interval = setInterval(update, 500);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      clearInterval(interval);
    };
  }, [currentStep, isPhone, mode, stepKey]);

  // ── Analytics + focus once a step is on screen ──
  useEffect(() => {
    if (!stepKey || !currentStep || mode === 'locating') return undefined;
    if (viewedRef.current !== stepKey) {
      viewedRef.current = stepKey;
      trackTourStepViewed(currentStep.id, { stepIndex: currentStepIndex, role, presentation: mode });
    }
    if (!isWeb) return undefined;
    // After the card's fade-in has started: the entering animation keeps the
    // card unfocusable for its first frame.
    const timer = setTimeout(() => {
      const node = cardRef.current as unknown as { focus?: (options?: { preventScroll?: boolean }) => void } | null;
      node?.focus?.({ preventScroll: true });
    }, reduceMotion ? 0 : 200);
    return () => clearTimeout(timer);
  }, [currentStep, currentStepIndex, mode, reduceMotion, role, stepKey]);

  const isLastStep = currentStepIndex >= totalSteps - 1;

  const handleNext = useCallback(() => {
    if (isLastStep && returnTo && normalizePath(returnTo) !== normalizePath(pathnameRef.current)) {
      router.push(returnTo as never);
    }
    nextStep();
  }, [isLastStep, nextStep, returnTo, router]);

  // ── Esc skips the tour ──
  useEffect(() => {
    if (!isWeb || !stepKey) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        skipTutorial();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [skipTutorial, stepKey]);

  if (!stepKey || !currentStep || !isTourRoute(pathname)) return null;

  const viewport = { width: viewportWidth, height: viewportHeight };
  let spotlight: TourRect | null = null;
  if (mode === 'spotlight' && rect) {
    // Padded, then clipped to the viewport so the ring never runs off screen.
    const x = Math.max(0, rect.x - SPOTLIGHT_PADDING);
    const y = Math.max(0, rect.y - SPOTLIGHT_PADDING);
    const right = Math.min(viewport.width, rect.x + rect.width + SPOTLIGHT_PADDING);
    const bottom = Math.min(viewport.height, rect.y + rect.height + SPOTLIGHT_PADDING);
    spotlight = { x, y, width: Math.max(0, right - x), height: Math.max(0, bottom - y) };
  }
  const placement = placeTourCard(spotlight, viewport, cardHeight, isPhone);
  const stepCopy = `guidedTour.steps.${currentStep.id}`;
  const showCard = mode !== 'locating';

  return (
    <View style={styles.root} pointerEvents="box-none" testID="guided_tour_overlay">
      {spotlight ? (
        <>
          <View style={[styles.dim, { top: 0, left: 0, right: 0, height: spotlight.y }]} />
          <View style={[styles.dim, { top: spotlight.y + spotlight.height, left: 0, right: 0, bottom: 0 }]} />
          <View style={[styles.dim, { top: spotlight.y, left: 0, width: spotlight.x, height: spotlight.height }]} />
          <View
            style={[styles.dim, { top: spotlight.y, left: spotlight.x + spotlight.width, right: 0, height: spotlight.height }]}
          />
          {/* Transparent blocker: the tour is read-and-continue, not click-through. */}
          <View
            testID="guided_tour_spotlight"
            style={[styles.ring, {
              left: spotlight.x,
              top: spotlight.y,
              width: spotlight.width,
              height: spotlight.height,
            }]}
          />
        </>
      ) : (
        <View style={[styles.dim, StyleSheet.absoluteFillObject]} />
      )}

      {showCard && (
        <Animated.View
          key={stepKey}
          entering={reduceMotion ? undefined : FadeIn.duration(160)}
          style={[styles.cardPosition, { left: placement.left, top: placement.top, width: placement.width }]}
          onLayout={(event) => {
            const next = Math.round(event.nativeEvent.layout.height);
            if (next > 0 && Math.abs(next - cardHeight) > 2) setCardHeight(next);
          }}
        >
          <View
            ref={cardRef}
            testID="guided_tour_card"
            role="dialog"
            aria-modal
            aria-labelledby="guided_tour_title"
            aria-label={t('guidedTour.dialogLabel')}
            focusable
            style={styles.card}
          >
            <View style={styles.header} testID={`guided_tour_step_${currentStep.id}`}>
              <Text style={styles.counter} testID="guided_tour_counter">
                {t('guidedTour.counter', { current: currentStepIndex + 1, total: totalSteps })}
              </Text>
              <Pressable
                onPress={skipTutorial}
                accessibilityRole="button"
                accessibilityLabel={t('guidedTour.skip')}
                hitSlop={10}
                testID="guided_tour_close"
                style={styles.iconButton}
              >
                <MaterialIcons name="close" size={20} color={reviewDesk.inkSoft} />
              </Pressable>
            </View>
            <View style={styles.progressTrack}>
              <View
                style={[styles.progressFill, { width: `${Math.round(((currentStepIndex + 1) / Math.max(totalSteps, 1)) * 100)}%` }]}
              />
            </View>

            <Text nativeID="guided_tour_title" role="heading" style={styles.title}>
              {t(`${stepCopy}.title`)}
            </Text>
            <Text style={styles.body}>{t(`${stepCopy}.body`)}</Text>
            {mode === 'card' && (
              <View style={styles.where} testID="guided_tour_where">
                <MaterialIcons name="place" size={18} color={reviewDesk.accent} />
                <Text style={styles.whereText}>
                  <Text style={styles.whereLabel}>{t('guidedTour.whereLabel')}: </Text>
                  {t(`${stepCopy}.where`)}
                </Text>
              </View>
            )}

            <View style={styles.actions}>
              {!isLastStep && (
                <Pressable
                  onPress={skipTutorial}
                  accessibilityRole="button"
                  testID="guided_tour_skip"
                  style={styles.linkButton}
                >
                  <Text style={styles.linkText}>{t('guidedTour.skip')}</Text>
                </Pressable>
              )}
              <View style={styles.spacer} />
              <Pressable
                onPress={prevStep}
                disabled={currentStepIndex === 0}
                accessibilityRole="button"
                accessibilityState={{ disabled: currentStepIndex === 0 }}
                testID="guided_tour_back"
                style={[styles.secondaryButton, currentStepIndex === 0 && styles.buttonDisabled]}
              >
                <Text style={styles.secondaryText}>{t('guidedTour.back')}</Text>
              </Pressable>
              <Pressable
                onPress={handleNext}
                accessibilityRole="button"
                testID="guided_tour_next"
                style={styles.primaryButton}
              >
                <Text style={styles.primaryText}>{isLastStep ? t('guidedTour.finish') : t('guidedTour.next')}</Text>
              </Pressable>
            </View>
          </View>
        </Animated.View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    ...StyleSheet.absoluteFillObject,
    ...(isWeb ? ({ position: 'fixed' } as object) : null),
    zIndex: 10000,
  },
  dim: {
    position: 'absolute',
    backgroundColor: DIM,
  },
  ring: {
    position: 'absolute',
    borderRadius: 12,
    borderWidth: 3,
    borderColor: '#F59E0B',
    backgroundColor: 'transparent',
  },
  cardPosition: {
    position: 'absolute',
  },
  card: {
    backgroundColor: reviewDesk.card,
    borderRadius: 14,
    padding: 18,
    gap: 10,
    shadowColor: '#0F172A',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.22,
    shadowRadius: 24,
    elevation: 12,
    ...(isWeb ? ({ outlineStyle: 'none' } as object) : null),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  counter: {
    color: reviewDesk.accent,
    fontSize: 12,
    fontWeight: '800',
    letterSpacing: 0.3,
  },
  iconButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  progressTrack: {
    height: 4,
    borderRadius: 2,
    backgroundColor: reviewDesk.line,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: reviewDesk.accent,
  },
  title: {
    color: reviewDesk.ink,
    fontSize: 17,
    fontWeight: '800',
    lineHeight: 23,
  },
  body: {
    color: reviewDesk.inkSoft,
    fontSize: 14,
    lineHeight: 21,
  },
  where: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'flex-start',
    backgroundColor: reviewDesk.accentWash,
    borderRadius: 10,
    padding: 10,
  },
  whereText: {
    flex: 1,
    color: reviewDesk.ink,
    fontSize: 13,
    lineHeight: 19,
  },
  whereLabel: {
    fontWeight: '800',
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 4,
  },
  spacer: {
    flex: 1,
  },
  linkButton: {
    minHeight: 40,
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
  linkText: {
    color: reviewDesk.inkSoft,
    fontSize: 14,
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
  secondaryButton: {
    minHeight: 40,
    paddingHorizontal: 14,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    justifyContent: 'center',
  },
  secondaryText: {
    color: reviewDesk.ink,
    fontSize: 14,
    fontWeight: '700',
  },
  buttonDisabled: {
    opacity: 0.45,
  },
  primaryButton: {
    minHeight: 40,
    paddingHorizontal: 16,
    borderRadius: 10,
    backgroundColor: reviewDesk.accent,
    justifyContent: 'center',
  },
  primaryText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '800',
  },
});

export default TutorialLayer;
